<template>
    <div :class="classes">
        <div @click.stop>
            <hox-mentionable
                :model-value="internalContent"
                :members="members"
                :poptip-width="poptipWidth"
                @insert="onInsert"
            >
                <template #item="s">
                    <span v-if="s.item.alias">
                        <strong>{{ s.item.label }}</strong>
                    </span>
                    <span v-else v-text="s.item.label"></span>
                </template>

                <div ref="editor" class="qa-annotation-input-form__input" contenteditable @input="onDivInput" />
            </hox-mentionable>

            <Tooltip v-if="mentioned.length" placement="bottom-start">
                <span class="link-like">{{ mentionedCountText }}</span>
                will be notified
                <template #content>
                    <p v-for="user in mentioned" :key="user">{{ user }}</p>
                </template>
            </Tooltip>
            <p v-else>
                Add
                <span class="mentionable-tag">@</span>
                to mention someone
            </p>
        </div>

        <div class="qa-annotation-input-form__buttons-container">
            <Button class="qa-annotation-input-form__button" :disabled="isSaving" type="default" @click.stop="cancel">
                <slot name="cancelButtonContent">Cancel</slot>
            </Button>
            <Button
                class="qa-annotation-input-form__button"
                :disabled="hasValidationErrors"
                :loading="isSaving"
                type="primary"
                @click.stop="save"
            >
                <slot name="saveButtonContent">Send</slot>
            </Button>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import unique from "hoxton-shared/utils/unique";

import HoxMentionable from "@/components/common/Mentionable.vue";
import { useMentionable } from "@/composables/useMentionable";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

function processTextForEdit(text: string) {
    let value = text;

    value = value.replace(/\n+\s+\n+/g, "\n\n");
    value = value.replace(/\n\n+/g, "\n\n");
    value = value.replace(/\n/g, "<br>");
    value = value.replace(/\s+/g, " ");

    return value;
}

export default defineComponent({
    components: { HoxMentionable },

    props: {
        content: {
            type: String,
            default: ""
        },
        isSaving: {
            type: Boolean
        },
        marginBottom: {
            type: String as PropType<"none" | "small">,
            default: "small",
            validator(value: string) {
                return ["none", "small"].includes(value);
            }
        },
        poptipWidth: {
            type: String as PropType<"normal" | "wide">,
            default: "normal",
            validator(value: string) {
                return ["normal", "wide"].includes(value);
            }
        }
    },

    emits: ["cancel", "save"],

    setup() {
        const { members, validMentions } = useMentionable();
        const ga4 = useGA4Tracking();

        return { members, validMentions, ga4 };
    },

    data() {
        return {
            internalContent: "",
            mentioned: [] as string[]
        };
    },
    computed: {
        classes() {
            const classes = ["qa-annotation-input-form"];
            if (this.marginBottom === "small") {
                classes.push("qa-annotation-input-form--margin-bottom-small");
            }
            return classes;
        },

        hasValidationErrors() {
            return !this.internalContent;
        },

        mentionedCountText() {
            return `${this.mentioned.length} ${this.mentioned.length === 1 ? "person" : "people"}`;
        },

        mentionedUserIds() {
            return this.members.filter(({ value }) => this.mentioned.includes(`@${value}`)).map(({ label }) => label);
        }
    },
    watch: {
        content: {
            handler() {
                this.internalContent = processTextForEdit(this.content);
            },
            immediate: true
        }
    },
    mounted() {
        this.focus();
    },
    methods: {
        focus() {
            (this.$refs.editor as HTMLElement).focus();
        },

        cancel() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.QA_DRAWER,
                featureContext: GA4.FEATURE_CONTEXTS.CANCEL_ADD_ANNOTATION
            });
            this.$emit("cancel");
        },

        onDivInput(e: InputEvent) {
            this.internalContent = (e.target as HTMLElement).innerHTML;
            this.updateMentioned((e.target as HTMLElement).innerHTML);
        },

        onInsert() {
            this.updateMentioned((this.$refs.editor as HTMLElement).innerHTML);
        },

        save() {
            let html = this.processTextForSave((this.$refs.editor as HTMLElement).innerHTML);

            // We need to add additional spaces around the mentions tag
            html = html.replace(
                /<span class="mentionable-tag" [^>]*>(@[\w.\-@]+)<\/span>/,
                (match: string) => ` ${match} `
            );

            const div = document.createElement("div");
            div.innerHTML = html;
            const text = div.textContent || div.innerText || "";

            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                featureCategory: GA4.FEATURE_CATEGORIES.QA_DRAWER,
                featureContext: GA4.FEATURE_CONTEXTS.ADD_NEW_ANNOTATION
            });

            this.$emit("save", {
                content: text.replace("  ", " "),
                mentions: this.mentionedUserIds
            });
        },

        processTextForSave(html: string) {
            // https://gist.github.com/nathansmith/86b5d4b23ed968a92fd4
            let value = html;
            // Convert `&amp;` to `&`.
            value = value.replace(/&amp;/gi, "&");

            // Replace spaces.
            value = value.replace(/&nbsp;/gi, " ");

            // Tighten up "<" and ">".
            value = value.replace(/>\s+/g, ">");
            value = value.replace(/\s+</g, "<");

            // Replace "<div><br></div>" - new line in chrome
            value = value.replace(/<div><br><\/div>/gi, "\n");

            // Replace "<br>".
            value = value.replace(/<br>/gi, "\n");

            // Replace "<div>" (from Chrome).
            value = value.replace(/<div>/gi, "\n");
            value = value.replace(/<\/div>/gi, "");

            // No more than 2x newline, per "paragraph".
            value = value.replace(/\n\n+/g, "\n\n");

            return value;
        },

        getAllMatches(regExp: RegExp, str: string) {
            const matches = [];

            while (true) {
                const match = regExp.exec(str);
                if (match === null) {
                    break;
                }
                matches.push(match[3]);
            }
            return matches;
        },

        updateMentioned(text: string) {
            let parsed = this.getAllMatches(/((^|\s|>|;))(@[\w.\-@]+)/g, text);

            if (parsed.includes("@campaign")) {
                parsed = ([] as string[]).concat(
                    parsed.filter(i => i !== "@campaign"),
                    this.members.map(({ value }) => `@${value}`)
                );
            }
            const uniqueMentions = unique(parsed);

            this.mentioned = uniqueMentions.filter(mention => this.validMentions.includes(mention));
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.qa-annotation-input-form {
    padding: $spacing 2px 0 2px;

    &--margin-bottom-small {
        margin: 0 0 $spacing-small;
    }

    .atwho-view {
        max-height: 86px;
    }
}

.qa-annotation-input-form__header {
    font-size: 18px;
    line-height: 1em;
    margin: 0 32px 16px 0;
    padding: 16px 0;
}

.qa-annotation-input-form__input {
    min-height: 6em;
    width: 100%;
    border: 1px solid $grey3;
    white-space: pre-wrap;
    margin-bottom: $spacing-small;
    padding: 6px;
    outline-color: white;

    &:focus {
        border: 1px solid $grey5;
    }
}

.qa-annotation-input-form .qa-annotation-input-form__button {
    margin: 0 0 0 $spacing-small;

    .ivu-icon-ios-loading {
        position: absolute;
        right: calc(50% - 10px);
        top: calc(50% - 10px);
    }

    &.ivu-btn > .ivu-icon + span {
        margin-left: 0;
    }
}

.qa-annotation-input-form__buttons-container {
    display: flex;
    justify-content: flex-end;
    padding: $spacing-small 0 0 0;
}

.mentionable-usage-description__icon {
    color: $grey5;
    margin-bottom: $spacing;
}

.mentionable-tag {
    color: $blue;
}
</style>
